import { startCase } from 'lodash';
import { type Schema, ValidationError } from 'yup';
import type { MissingFieldsData } from '@/state/navigation';
import type { ContactsFormData, ContactType } from './types';

// Must match the Contacts sidebar link label — the badge popup filters rows by
// `field.section === link.label`.
const CONTACTS_SECTION_LABEL = 'Contacts';

// `contacts.client[0].trust.trustName` → role, index, relative field path.
const PATH_RE = /^contacts\.([a-zA-Z]+)\[(\d+)\]\.(.+)$/;

const FIELD_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  licenseNumber: 'Agent License Number',
  'brokerage.companyName': 'Brokerage Name',
  'brokerage.licenseNumber': 'Brokerage License Number',
  'trust.trustName': 'Trust Name',
  'entity.entityName': 'Entity Name',
  'probate.estateName': 'Estate Name',
  'trust.title': 'Client Title',
  'entity.title': 'Client Title',
  'probate.title': 'Client Title',
  'trust.titleOther': 'Client Title (Other)',
  'entity.titleOther': 'Client Title (Other)',
  'probate.titleOther': 'Client Title (Other)',
  'powerOfAttorney.firstName': 'Authorized Signer First Name',
  'powerOfAttorney.lastName': 'Authorized Signer Last Name',
  'powerOfAttorney.email': 'Authorized Signer Email',
};

const humanize = (field: string): string =>
  startCase(field.split('.').pop() ?? field);

export interface MissingContactFieldsOptions {
  schema: Schema;
  /** Role label per interaction type (badge popup row title). */
  roleLabels: Record<ContactType, string>;
}

/**
 * Missing required contact fields for the sidebar badge + documents lockdown,
 * derived by validating the hydrated contacts against the per-interaction Yup
 * schema (so ownership-entity conditionals surface exactly as the form enforces
 * them).
 */
export function computeMissingContactFields(
  formData: ContactsFormData,
  { schema, roleLabels }: MissingContactFieldsOptions,
): MissingFieldsData[] {
  try {
    schema.validateSync(formData, { abortEarly: false });
    return [];
  } catch (err) {
    if (!(err instanceof ValidationError)) throw err;
    return err.inner.flatMap((e) => {
      const match = e.path?.match(PATH_RE);
      if (!match) return [];
      const [, role, indexStr, field] = match;
      const roleCount = formData.contacts[role as ContactType]?.length ?? 0;
      return [
        {
          section: CONTACTS_SECTION_LABEL,
          cardTitle: roleLabels[role as ContactType] ?? role,
          label: FIELD_LABELS[field] ?? humanize(field),
          name: `contacts.${role}.${indexStr}.${field}`,
          index: Number(indexStr),
          isAddable: true,
          showIndex: roleCount > 1,
        },
      ];
    });
  }
}
